import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import { checkIsManagerGroup } from "@repo/app-types/entities/group";
import {
  asTeammateId,
  buildTeammateDisplayNames,
  getTeammateLicenseTypeForProductKey,
  type TeammateId,
  type TeammateLicenseType,
} from "@repo/app-types/entities/teammate";
import type {
  PredefinedTeammateRoleType,
  TeammatePermissions,
  TeammateRoleType,
} from "@repo/app-types/entities/teammatePermissions";
import {
  allTeammateRoleTypes,
  isPredefinedTeammateRoleType,
  PermissionActions,
  PermissionSubjects,
  predefinedRoleNameByType,
  TeammateRoleTypes,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { isPerformanceCompany } from "@repo/app-types/helpers/billingFeatureGating";
import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { dropdownMenu } from "@repo/ui/Dropdown/dropdownMenuStyles";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { Pill } from "@repo/ui/Pills/pill";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Switch } from "@repo/ui/Switch/switch";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { EntityTable } from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconDotsVertical,
  IconLoader2,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Menu, MenuItem, MenuTrigger, Popover } from "react-aria-components";
import { v4 as uuid } from "uuid";
import {
  useCompanyDomainsQuery,
  useLicenseUtilizationQuery,
} from "../../../api/entities/company.js";
import {
  useDeleteCompanyInvitationMutation,
  useInviteTeammatesMutation,
  usePendingInvitationsPaginatedInfiniteQuery,
} from "../../../api/entities/companyInvitation.js";
import {
  useAllTeammateGroupsQuery,
  useSetTeammateManagerMutation,
} from "../../../api/entities/group.js";
import {
  useTeammatesPaginatedInfiniteQuery,
  useUpdateTeammateMutation,
} from "../../../api/entities/teammate.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  selectCompany,
  selectIsAlpharunWorkspace,
  selectIsRecruitingModeCompany,
  selectIsUsingSSO,
} from "../../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import {
  teammateAdded,
  teammateRemoved,
  teammateUpdated,
} from "../../teammates/teammatesSlice.js";
import { buildInviteFailureMessage } from "./inviteFailures.js";
import { InviteTeammatesSection } from "./inviteTeammatesSection.js";
import { TeammateLicenseCell } from "./teammateLicenseCell.js";
import {
  getLicenseTypeOptions,
  teammateLicenseCopy,
} from "./teammateLicenses.js";
import { TeammateManagerCell } from "./teammateManagerCell.js";

const roleOptions = allTeammateRoleTypes
  .filter(isPredefinedTeammateRoleType)
  .map((role) => ({
    label: predefinedRoleNameByType[role],
    value: role,
  }));

interface InvitationRow {
  id: string;
  email: string;
  role: TeammateRoleType;
  dateAdded: string;
}

interface TeammateRow {
  id: string;
  email: string;
  name: string;
  role: TeammateRoleType;
  permissions: TeammatePermissions;
  licenseType: TeammateLicenseType;
  managerId: TeammateId | null | undefined;
  dateAdded: string;
  /** Has signed in at least once, so the account is usable. */
  isConnected: boolean;
  hasPendingInvitation: boolean;
  isDeactivated: boolean;
}

/**
 * A teammate nobody can let in yet. `GET /user` puts a session into a company
 * through a pending invitation, SSO auto-join, or an approved email domain —
 * never through the teammate row itself — so without one of those an invitation
 * is the only door, and until somebody sends one this row is a dead end.
 *
 * Having never signed in is deliberately not worth a pill of its own: when the
 * person could sign in unaided, the row needs nothing from an admin and so
 * reads the same as any other.
 */
const NotInvitedPill: FC = () => (
  <TooltipTrigger>
    <TooltipHoverTarget shape="rounded">
      <Pill label="Not invited" color={Color.Gray} size={Size.SMALL} />
    </TooltipHoverTarget>
    <SimpleTooltip>
      Added to Alpharun but never invited, so they can&apos;t sign in yet.
    </SimpleTooltip>
  </TooltipTrigger>
);

interface InvitedPillProps {
  tooltip: string;
}

/** A teammate with an invitation they haven't accepted yet. */
const InvitedPill: FC<InvitedPillProps> = (props) => {
  const { tooltip } = props;
  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">
        <Pill label="Invited" color={Color.Yellow} size={Size.SMALL} />
      </TooltipHoverTarget>
      <SimpleTooltip>{tooltip}</SimpleTooltip>
    </TooltipTrigger>
  );
};

/**
 * The lowercased email domains the company has approved for self-signup, or how
 * far the answer is from being known.
 */
type ApprovedDomains = "loading" | "unavailable" | ReadonlySet<string>;

interface TeammateStatusPillsProps {
  row: TeammateRow;
  /** Access comes from the identity provider, so nobody is ever invited. */
  isUsingSSO: boolean;
  approvedDomains: ApprovedDomains;
}

/**
 * How far along a teammate is towards actually being able to use Alpharun.
 * Someone can be provisioned (by a CSV import, an HQ action, or a call
 * recording upload) without ever having been invited, and can be invited
 * without having accepted — both look identical in the roster otherwise, which
 * is what used to send admins chasing sign-in failures for people who had
 * simply never been invited.
 */
const TeammateStatusPills: FC<TeammateStatusPillsProps> = (props) => {
  const { row, isUsingSSO, approvedDomains } = props;

  // An outstanding invitation on a deactivated person is state, not a way back
  // in, because no invitation reactivates anybody.
  if (row.isDeactivated) {
    return (
      <>
        <Pill label="Deactivated" color={Color.Red} size={Size.SMALL} />
        {row.hasPendingInvitation ? (
          <InvitedPill tooltip="Invited but hasn't accepted yet. Accepting won't reactivate them — use Reactivate in this row's actions menu first." />
        ) : null}
      </>
    );
  }

  if (row.hasPendingInvitation && !row.isConnected) {
    return (
      <InvitedPill tooltip="Invited but hasn't signed in yet. Changing their role still applies once they accept." />
    );
  }

  // Nobody has claimed this row yet, so the only question is whether anybody
  // has to do something about it.
  if (!row.isConnected) {
    // An identity provider lets them in on their own, so there is nothing to
    // say and nothing to wait on the domains for.
    if (isUsingSSO) {
      return null;
    }

    if (approvedDomains === "loading") {
      return <PillSkeleton size={Size.SMALL} />;
    }

    // Without the domains, claiming an invitation is needed could be wrong, and
    // a skeleton would pulse forever.
    if (approvedDomains === "unavailable") {
      return null;
    }

    // An approved email domain is a door of their own, so again nothing to say.
    return approvedDomains.has(
      row.email.slice(row.email.lastIndexOf("@") + 1).toLowerCase(),
    ) ? null : (
      <NotInvitedPill />
    );
  }

  return null;
};

interface TeammateMoreMenuProps {
  row: TeammateRow;
  isCurrentTeammate: boolean;
  /** Access comes from the identity provider, so there is nothing to invite. */
  isUsingSSO: boolean;
  /** A row action (invite or reactivate) is already in flight. */
  isPending: boolean;
  onInvite: () => void;
  onReactivate: () => void;
  onDeactivate: () => void;
}

/** The actions that change a teammate's access, if any apply to this row. */
const TeammateMoreMenu: FC<TeammateMoreMenuProps> = (props) => {
  const {
    row,
    isCurrentTeammate,
    isUsingSSO,
    isPending,
    onInvite,
    onReactivate,
    onDeactivate,
  } = props;

  // Inviting only means something for somebody who has neither claimed the row
  // nor been asked to. An approved email domain deliberately doesn't hide this:
  // they could sign in unaided, but an invitation is still the only way to tell
  // them Alpharun exists.
  const canInvite =
    !row.isConnected &&
    !row.hasPendingInvitation &&
    !row.isDeactivated &&
    !isUsingSSO;
  const canDeactivate = !row.isDeactivated && !isCurrentTeammate;

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.TEAMMATES}
      passThrough={true}
    >
      {(allowed) =>
        // Nothing applies to your own signed-in row, and nothing at all applies
        // without permission to change teammates, so there is no menu to open.
        allowed && (canInvite || row.isDeactivated || canDeactivate) ? (
          <MenuTrigger>
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={
                isPending ? (
                  <IconLoader2 className="size-5 animate-spin text-muted-foreground" />
                ) : (
                  <IconDotsVertical className="size-5" />
                )
              }
              isDisabled={isPending}
              accessibleLabel="Teammate actions"
            />

            <Popover
              className={dropdownMenu().popover()}
              placement="bottom end"
            >
              <Menu className={dropdownMenu().menu()}>
                {canInvite ? (
                  <MenuItem
                    className={dropdownMenu().item()}
                    onAction={onInvite}
                  >
                    Invite
                  </MenuItem>
                ) : null}
                {/*
                  Reactivating keeps the role they already have and sends
                  nothing: somebody deactivated was re-hired or came back.
                */}
                {row.isDeactivated ? (
                  <MenuItem
                    className={dropdownMenu().item()}
                    onAction={onReactivate}
                  >
                    Reactivate
                  </MenuItem>
                ) : null}
                {canDeactivate ? (
                  <MenuItem
                    className={dropdownMenu().item()}
                    onAction={onDeactivate}
                  >
                    Deactivate
                  </MenuItem>
                ) : null}
              </Menu>
            </Popover>
          </MenuTrigger>
        ) : null
      }
    </Can>
  );
};

export const TeammatesTab: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const currentTeammate = useAppSelector(selectTeammate);
  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);

  const isPerformance = company ? isPerformanceCompany(company) : false;
  const { data: licenseUtilization, isLoading: isLicenseUtilizationLoading } =
    useLicenseUtilizationQuery(isPerformance ? undefined : skipToken);

  // A never-signed-in row waits on this rather than guessing: an approved
  // domain is the difference between needing an invitation and not, so a "Not
  // invited" that disappears once the domains land misleads whoever read it.
  const { data: companyDomains, isError: isCompanyDomainsError } =
    useCompanyDomainsQuery();
  const approvedDomains: ApprovedDomains = useMemo(() => {
    if (!companyDomains) {
      return isCompanyDomainsError ? "unavailable" : "loading";
    }
    return new Set(companyDomains.map((domain) => domain.toLowerCase()));
  }, [companyDomains, isCompanyDomainsError]);

  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );
  const hasGroups = hasTeamPerformanceTab;

  // The license column only exists when the contract sells two or more
  // performance license tiers; the picker offers exactly the entitled tiers.
  const licenseTypeOptions = useMemo(
    () => getLicenseTypeOptions(licenseUtilization?.productUtilization ?? []),
    [licenseUtilization],
  );
  const showLicenseColumn = licenseTypeOptions.length >= 2;

  const [deleteCompanyInvitation] = useDeleteCompanyInvitationMutation();
  const [inviteTeammates] = useInviteTeammatesMutation();
  const [updateTeammate] = useUpdateTeammateMutation();
  const [setTeammateManager] = useSetTeammateManagerMutation();

  const { data: groups } = useAllTeammateGroupsQuery(
    hasGroups ? { includeDeactivated: true } : skipToken,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const [includeDeactivated, setIncludeDeactivated] = useState(false);

  const [selectedSubTabIndex, setSelectedSubTabIndex] = useState(0);

  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(
    null,
  );
  const [teammateToDeactivate, setTeammateToDeactivate] =
    useState<TeammateId | null>(null);
  // One row action (invite or reactivate) can be in flight per row at a time,
  // and rows act independently, so this tracks every id currently busy rather
  // than the most recent one.
  const [pendingTeammateIds, setPendingTeammateIds] = useState<
    ReadonlySet<string>
  >(new Set());

  // Paginated invitations query. The deactivated switch is deliberately not
  // wired in here: it only concerns the teammates tab.
  const {
    data: invitationsData,
    isLoading: isInvitationsLoading,
    isError: isInvitationsError,
    hasNextPage: hasNextInvitationsPage,
    fetchNextPage: fetchNextInvitationsPage,
    isFetchingNextPage: isFetchingNextInvitationsPage,
  } = usePendingInvitationsPaginatedInfiniteQuery({
    search: debouncedSearch || undefined,
  });

  // Paginated teammates query
  const {
    data: teammatesData,
    isLoading: isTeammatesLoading,
    isError: isTeammatesError,
    hasNextPage: hasNextTeammatesPage,
    fetchNextPage: fetchNextTeammatesPage,
    isFetchingNextPage: isFetchingNextTeammatesPage,
  } = useTeammatesPaginatedInfiniteQuery({
    search: debouncedSearch || undefined,
    includeDeactivated,
  });

  // Hide internal Alpharun accounts from customers; only show them in our own
  // workspaces.
  const isAlpharunWorkspace = useAppSelector(selectIsAlpharunWorkspace);
  const isUsingSSO = useAppSelector(selectIsUsingSSO);

  const invitations = useMemo(
    () => invitationsData?.pages.flatMap((page) => page.invitations) ?? [],
    [invitationsData],
  );

  const teammates = useMemo(() => {
    const allTeammates =
      teammatesData?.pages.flatMap((page) => page.teammates) ?? [];
    return isAlpharunWorkspace
      ? allTeammates
      : allTeammates.filter((t) => !isAlpharunEmail(t.email));
  }, [teammatesData, isAlpharunWorkspace]);

  // Build invitation rows
  const invitationRows: readonly InvitationRow[] = useMemo(
    () =>
      invitations.map((inv) => ({
        id: inv.id,
        email: inv.recipient_email,
        role: inv.metadata.permissions.role.type,
        dateAdded: inv.created_at,
      })),
    [invitations],
  );

  // Build manager lookup
  const getManagerId = useMemo(() => {
    if (!groups) {
      return () => undefined;
    }

    const map = new Map<TeammateId, TeammateId>();

    for (const group of groups) {
      if (!checkIsManagerGroup(group)) {
        continue;
      }

      for (const teammateId of group.teammate_ids) {
        map.set(teammateId, group.manager_id);
      }
    }

    return (teammateId: TeammateId) => map.get(teammateId) ?? null;
  }, [groups]);

  // Build teammate rows
  const teammateRows: readonly TeammateRow[] = useMemo(() => {
    return teammates.map((t) => {
      const managerId = getManagerId(t.id);
      return {
        id: t.id,
        email: t.email,
        name: buildTeammateDisplayNames(t).primaryDisplayName,
        role: t.permissions.role.type,
        permissions: t.permissions,
        licenseType: t.license_type,
        managerId,
        dateAdded: t.created_at,
        isConnected: t.auth_user_id !== null,
        hasPendingInvitation: t.has_pending_invitation,
        isDeactivated: t.deactivated_at !== null,
      };
    });
  }, [teammates, getManagerId]);

  const filteredRoleOptions = useMemo(
    () =>
      roleOptions.filter((role) => {
        if (role.value === TeammateRoleTypes.TEAM_MEMBER) {
          return hasTeamPerformanceTab;
        }
        return true;
      }),
    [hasTeamPerformanceTab],
  );

  const onDeleteInvitation = async (invitationId: string) => {
    try {
      await deleteCompanyInvitation(invitationId).unwrap();
      showNotification(dispatch, {
        id: `invitation-deleted-${invitationId}`,
        primaryMessage: "Invitation cancelled",
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: `invitation-delete-failed-${invitationId}`,
        primaryMessage: "Failed to cancel invitation",
        type: NotificationType.FAILURE,
      });
    }
    setInvitationToDelete(null);
  };

  const inviteTeammate = useCallback(
    async (row: TeammateRow) => {
      const notificationId = uuid();
      setPendingTeammateIds((ids) => new Set(ids).add(row.id));
      try {
        const result = await inviteTeammates({
          invites: [
            {
              email: row.email,
              permissions: row.permissions,
              // No `license_type`: the teammate row already exists and already
              // holds its tier, so the invitation's license type — which only
              // applies when accepting creates a brand-new teammate — would
              // change nothing on accept, while putting the invite through the
              // endpoint's contract-line gate for a license already in use.
            },
          ],
        }).unwrap();

        // The endpoint answers 200 for "created nothing" too, so the outcome
        // has to be read off the result rather than the status.
        if (result.company_invitations.length > 0) {
          showNotification(dispatch, {
            id: notificationId,
            primaryMessage: `Invitation sent to ${row.email}`,
            type: NotificationType.SUCCESS,
          });
        } else if ((result.deactivated_emails ?? []).length > 0) {
          // Only reachable if they were deactivated after this page loaded —
          // a deactivated row offers Reactivate, not Invite.
          showNotification(dispatch, {
            id: notificationId,
            primaryMessage: `${row.email} is deactivated, so no invitation was sent. Turn on “Show deactivated teammates” to reactivate them.`,
            type: NotificationType.FAILURE,
          });
        } else if ((result.skipped_emails ?? []).length > 0) {
          showNotification(dispatch, {
            id: notificationId,
            primaryMessage: `${row.email} is already an active teammate who can sign in, so no invitation was needed`,
            type: NotificationType.FAILURE,
          });
        } else if (result.invalid_emails.length > 0) {
          showNotification(dispatch, {
            id: notificationId,
            primaryMessage: `No invitation was sent: ${row.email} is not a valid email address. Correct it wherever this teammate is managed.`,
            type: NotificationType.FAILURE,
          });
        } else {
          showNotification(dispatch, {
            id: notificationId,
            primaryMessage: "Failed to send invitation",
            type: NotificationType.FAILURE,
          });
        }
      } catch (error) {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: buildInviteFailureMessage(error),
          type: NotificationType.FAILURE,
        });
      }
      setPendingTeammateIds((ids) => {
        const remaining = new Set(ids);
        remaining.delete(row.id);
        return remaining;
      });
    },
    [dispatch, inviteTeammates],
  );

  // Bringing somebody back is immediate and sends nothing: they keep the role
  // their row already holds, and no invitation is involved.
  const reactivateTeammate = useCallback(
    async (row: TeammateRow) => {
      const notificationId = uuid();
      const teammateId = asTeammateId(row.id);
      setPendingTeammateIds((ids) => new Set(ids).add(row.id));
      try {
        const updatedTeammateResult = await updateTeammate({
          id: teammateId,
          patchPayload: { is_active: true },
        }).unwrap();
        // The mirror of the `teammateRemoved` a deactivation dispatches, so
        // pickers elsewhere in the app see them again without a full refetch.
        dispatch(teammateAdded(updatedTeammateResult));
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: `${row.email} reactivated`,
          type: NotificationType.SUCCESS,
        });
      } catch (error) {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: getApiErrorMessage(
            error,
            "Failed to reactivate teammate",
          ),
          type: NotificationType.FAILURE,
        });
      }
      setPendingTeammateIds((ids) => {
        const remaining = new Set(ids);
        remaining.delete(row.id);
        return remaining;
      });
    },
    [dispatch, updateTeammate],
  );

  const deactivateTeammate = async (teammateId: TeammateId) => {
    const notificationId = uuid();
    try {
      await updateTeammate({
        id: teammateId,
        patchPayload: { is_active: false },
      }).unwrap();
      dispatch(teammateRemoved(teammateId));
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: "Teammate deactivated",
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: "Teammate deactivation failed",
        type: NotificationType.FAILURE,
      });
    }
    setTeammateToDeactivate(null);
  };

  const updateTeammateRole = useCallback(
    async (teammateId: TeammateId, role: PredefinedTeammateRoleType) => {
      const notificationId = uuid();
      try {
        const updatedTeammateResult = await updateTeammate({
          id: teammateId,
          patchPayload: { permissions: { role: { type: role } } },
        }).unwrap();
        dispatch(
          teammateUpdated({ id: teammateId, changes: updatedTeammateResult }),
        );
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate role updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate role update failed",
          type: NotificationType.FAILURE,
        });
      }
    },
    [dispatch, updateTeammate],
  );

  const updateTeammateLicenseType = useCallback(
    async (teammateId: TeammateId, licenseType: TeammateLicenseType) => {
      const notificationId = uuid();
      try {
        const updatedTeammateResult = await updateTeammate({
          id: teammateId,
          patchPayload: { license_type: licenseType },
        }).unwrap();
        dispatch(
          teammateUpdated({ id: teammateId, changes: updatedTeammateResult }),
        );
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate license updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate license update failed",
          type: NotificationType.FAILURE,
        });
      }
    },
    [dispatch, updateTeammate],
  );

  const updateTeammateManager = useCallback(
    async (teammateId: TeammateId, managerId: TeammateId | null) => {
      const notificationId = uuid();
      try {
        await setTeammateManager({ teammateId, managerId }).unwrap();
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate manager updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate manager update failed",
          type: NotificationType.FAILURE,
        });
      }
    },
    [dispatch, setTeammateManager],
  );

  // Invitation table columns
  const invitationColumns: readonly EntityTableColumnDefinition<InvitationRow>[] =
    useMemo(
      () => [
        {
          id: "email",
          header: "Email",
          isRowHeader: true,
          renderCell: (row) => (
            <span className="text-sm text-foreground">{row.email}</span>
          ),
        },
        {
          id: "role",
          header: "Role",
          className: "w-0",
          renderCell: (row) => (
            <Pill
              label={
                isPredefinedTeammateRoleType(row.role)
                  ? predefinedRoleNameByType[row.role]
                  : row.role
              }
              size={Size.SMALL}
              color={Color.Gray}
            />
          ),
        },
        {
          id: "dateAdded",
          header: "Invited",
          className: "w-0",
          renderCell: (row) => (
            <span className="text-sm text-muted-foreground">
              {DateTime.fromISO(row.dateAdded).toFormat("MMM dd yyyy")}
            </span>
          ),
        },
        {
          id: "actions",
          header: "",
          className: "w-0",
          renderCell: (row) => (
            <div className="flex justify-end">
              <TooltipTrigger>
                <IconButton
                  variant={ButtonVariant.Secondary}
                  icon={<IconTrash className="h-4 w-4" />}
                  onClick={() => setInvitationToDelete(row.id)}
                  accessibleLabel="Cancel invitation"
                />
                <SimpleTooltip>Cancel invitation</SimpleTooltip>
              </TooltipTrigger>
            </div>
          ),
        },
      ],
      [],
    );

  // Teammate table columns
  const teammateColumns: readonly EntityTableColumnDefinition<TeammateRow>[] =
    useMemo(
      () => [
        {
          id: "name",
          header: "Name",
          isRowHeader: true,
          renderCell: (row) => (
            <div>
              {/*
                The pills wrap under the name rather than stretching the row:
                every other cell in the row is a single line high, and a taller
                name cell would make the roster ragged.
              */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-foreground">
                {row.name}
                <TeammateStatusPills
                  row={row}
                  isUsingSSO={isUsingSSO}
                  approvedDomains={approvedDomains}
                />
              </div>
              <div className="text-sm text-muted-foreground">{row.email}</div>
            </div>
          ),
        },
        ...(isRecruitingModeCompany && hasGroups
          ? [
              {
                id: "manager",
                header: "Manager",
                className: "w-0",
                renderCell: (row: TeammateRow) => (
                  <Can
                    I={PermissionActions.UPDATE}
                    a={PermissionSubjects.TEAMMATES}
                    passThrough={true}
                  >
                    {(allowed) => (
                      <TeammateManagerCell
                        managerId={row.managerId ?? null}
                        // A deactivated teammate cannot sign in, so there is
                        // nothing to configure for them.
                        isDisabled={!groups || !allowed || row.isDeactivated}
                        onChange={(managerId) => {
                          void updateTeammateManager(
                            asTeammateId(row.id),
                            managerId,
                          );
                        }}
                      />
                    )}
                  </Can>
                ),
              } satisfies EntityTableColumnDefinition<TeammateRow>,
            ]
          : []),
        // Only a company in recruiting mode manages roles from this roster.
        // That is deliberate: `isRecruitingModeCompany` is true for the
        // overwhelming majority of companies, performance ones included — see
        // `selectIsRecruitingModeCompany`.
        ...(isRecruitingModeCompany
          ? [
              {
                id: "role",
                header: "Role",
                className: "w-0",
                renderCell: (row: TeammateRow) => (
                  <Can
                    I={PermissionActions.UPDATE}
                    a={PermissionSubjects.TEAMMATES}
                    passThrough={true}
                  >
                    {(allowed) => (
                      // Every row holds a real role, including one imported or
                      // never invited: it is what the person receives the first
                      // time they sign in.
                      <SimpleSelect
                        label="Role"
                        isLabelHidden
                        value={row.role}
                        onChange={(option) => {
                          const changedRole =
                            option as PredefinedTeammateRoleType;
                          if (changedRole !== row.role) {
                            void updateTeammateRole(
                              asTeammateId(row.id),
                              changedRole,
                            );
                          }
                        }}
                        options={filteredRoleOptions}
                        // A deactivated teammate cannot sign in, so there is
                        // nothing to configure for them.
                        isDisabled={
                          !allowed ||
                          row.id === currentTeammate?.id ||
                          row.isDeactivated
                        }
                      />
                    )}
                  </Can>
                ),
              } satisfies EntityTableColumnDefinition<TeammateRow>,
            ]
          : []),
        ...(showLicenseColumn
          ? [
              {
                id: "license",
                header: "License",
                className: "w-0",
                renderCell: (row: TeammateRow) => (
                  <Can
                    I={PermissionActions.UPDATE}
                    a={PermissionSubjects.TEAMMATES}
                    passThrough={true}
                  >
                    {(allowed) => (
                      <TeammateLicenseCell
                        teammateName={row.name}
                        licenseType={row.licenseType}
                        options={licenseTypeOptions}
                        isDisabled={!allowed || row.isDeactivated}
                        onChange={(licenseType) => {
                          void updateTeammateLicenseType(
                            asTeammateId(row.id),
                            licenseType,
                          );
                        }}
                      />
                    )}
                  </Can>
                ),
              } satisfies EntityTableColumnDefinition<TeammateRow>,
            ]
          : []),
        {
          id: "dateAdded",
          header: "Date added",
          className: "w-0",
          renderCell: (row) => (
            <span className="text-sm text-muted-foreground">
              {DateTime.fromISO(row.dateAdded).toFormat("MMM dd yyyy")}
            </span>
          ),
        },
        {
          id: "actions",
          header: "",
          className: "w-0",
          renderCell: (row) => (
            <div className="flex justify-end">
              <TeammateMoreMenu
                row={row}
                isCurrentTeammate={row.id === currentTeammate?.id}
                isUsingSSO={isUsingSSO}
                isPending={pendingTeammateIds.has(row.id)}
                onInvite={() => {
                  void inviteTeammate(row);
                }}
                onReactivate={() => {
                  void reactivateTeammate(row);
                }}
                onDeactivate={() =>
                  setTeammateToDeactivate(asTeammateId(row.id))
                }
              />
            </div>
          ),
        },
      ],
      [
        isUsingSSO,
        approvedDomains,
        pendingTeammateIds,
        inviteTeammate,
        reactivateTeammate,
        isRecruitingModeCompany,
        hasGroups,
        groups,
        filteredRoleOptions,
        currentTeammate?.id,
        showLicenseColumn,
        licenseTypeOptions,
        updateTeammateManager,
        updateTeammateRole,
        updateTeammateLicenseType,
      ],
    );

  return (
    <>
      <div className="border-b border-gray-900/10 py-4">
        <h2 className="pb-4 text-lg leading-6 font-semibold text-foreground">
          Invite teammates
        </h2>
        <InviteTeammatesSection />
      </div>

      {isPerformance ? (
        isLicenseUtilizationLoading ? (
          <div className="py-4">
            <div className="h-6 w-48 animate-pulse rounded-sm bg-gray-200" />
          </div>
        ) : licenseUtilization ? (
          <div className="py-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">
                License utilization:
              </span>
              {licenseUtilization.productUtilization.map((product) => (
                <Pill
                  key={product.product_key}
                  label={`${
                    teammateLicenseCopy[
                      getTeammateLicenseTypeForProductKey(product.product_key)
                    ].label
                  }: ${product.used_licenses} / ${product.max_licenses}`}
                  size={Size.SMALL}
                  color={
                    product.used_licenses > product.max_licenses
                      ? Color.Red
                      : Color.Green
                  }
                />
              ))}
            </div>
            {licenseUtilization.productUtilization.some(
              (product) => product.used_licenses > product.max_licenses,
            ) && (
              <div className="mt-2 text-sm text-amber-600">
                You&apos;re using more licenses than your plan includes.
                Additional licenses are billed at the start of each month, or in
                accordance with your contract.
              </div>
            )}
          </div>
        ) : null
      ) : (
        <div className="py-4 text-sm text-muted-foreground">
          Your plan includes unlimited teammates.
        </div>
      )}

      <div className="py-4">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="max-w-xs flex-1">
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              leftIcon={<IconSearch className="h-4 w-4 text-gray-400" />}
              maxLength={100}
            />
          </div>
          {/*
            Only the teammates tab has a deactivated state to reveal; a pending
            invitation has none.
          */}
          {selectedSubTabIndex === 0 ? (
            <Switch
              label="Show deactivated teammates"
              isSelected={includeDeactivated}
              onChange={setIncludeDeactivated}
            />
          ) : null}
        </div>

        <div className="mb-4">
          {/*
            The first tab is "Teammates" rather than "Active teammates" because
            the switch above can bring deactivated people into it.
          */}
          <SmallTabs
            tabs={["Teammates", "Pending invitations"]}
            selectedTabIndex={selectedSubTabIndex}
            onSelectTabIndex={setSelectedSubTabIndex}
          />
        </div>

        <div className="overflow-x-auto">
          {selectedSubTabIndex === 0 ? (
            <EntityTable
              aria-label="Teammates"
              columns={teammateColumns}
              entities={teammateRows}
              isLoading={isTeammatesLoading}
              isError={isTeammatesError}
              onLoadMore={
                hasNextTeammatesPage && !isFetchingNextTeammatesPage
                  ? fetchNextTeammatesPage
                  : undefined
              }
              emptyMessage="No teammates found."
            />
          ) : (
            <EntityTable
              aria-label="Pending invitations"
              columns={invitationColumns}
              entities={invitationRows}
              isLoading={isInvitationsLoading}
              isError={isInvitationsError}
              onLoadMore={
                hasNextInvitationsPage && !isFetchingNextInvitationsPage
                  ? fetchNextInvitationsPage
                  : undefined
              }
              emptyMessage="No pending invitations."
            />
          )}
        </div>
      </div>

      <SimpleModal
        isOpen={Boolean(invitationToDelete)}
        onCancel={() => setInvitationToDelete(null)}
        variant={SimpleModalVariant.Warning}
        title="Revoke invitation"
        subtitle="Are you sure you want to cancel this invitation?"
        confirmButtonText="Cancel invitation"
        onConfirm={() => {
          if (invitationToDelete) {
            void onDeleteInvitation(invitationToDelete);
          }
        }}
      />
      <SimpleModal
        isOpen={Boolean(teammateToDeactivate)}
        onCancel={() => setTeammateToDeactivate(null)}
        variant={SimpleModalVariant.Warning}
        title="Deactivate teammate"
        subtitle="Deactivating this teammate will prevent them from logging in. They won't count toward your license usage. Turn on “Show deactivated teammates” to bring them back whenever they return."
        confirmButtonText="Deactivate teammate"
        onConfirm={() => {
          if (teammateToDeactivate) {
            void deactivateTeammate(teammateToDeactivate);
          }
        }}
      />
    </>
  );
};
